import { useState } from 'react'

import { formatClock, formatDayClock, NO_VALUE } from '../../lib/format.ts'
import { actionLabel, categoryOf, categoryPresentation } from '../../lib/logs/semantics.ts'
import { logKey } from '../../lib/sse/parse.ts'
import type { LogRow } from '../../lib/sse/types.ts'
import { EmptyState, Notice } from '../primitives/States.tsx'
import { StatusDot } from '../primitives/StatusDot.tsx'
import { TABLE, TD, TH, THEAD, TR } from '../primitives/table.ts'
import { EventDossier } from './EventDossier.tsx'

/** Acima disto, os dois relógios discordam o bastante para ser um sinal (§3.2). */
const SKEW_ALARM_S = 120

function skewSeconds(row: LogRow): number | null {
  const received = Date.parse(row.received_at)
  const event = Date.parse(row.event_time)
  if (!Number.isFinite(received) || !Number.isFinite(event)) return null
  return Math.round((received - event) / 1000)
}

/**
 * A lista de eventos — compartilhada pelo tail ao vivo e pelo histórico.
 *
 * Deixou de ser "timestamp | source | type | message". Cada linha comunica, de
 * relance, o TIPO de atividade (a categoria, por código legível sem cor), o QUE
 * aconteceu (o rótulo semântico do `event_type`), ONDE (host · empresa) e o
 * assunto reconhecível (canal · Event ID). O operador entende a linha sem abrir
 * o JSON (§1, §3, §5); um clique abre a ficha de investigação com os `facets` e
 * os pivôs (§6, §9).
 *
 * A linha NÃO carrega `facets` de propósito (§17): o resumo estruturado é
 * detalhe, carregado sob demanda, para a lista suportar centenas de milhões de
 * eventos sem parsear payload por linha. Por isso a categoria da lista vem do
 * `event_type` — o único sinal estruturado que a linha traz — e a ficha, aberta,
 * refina tudo a partir das colunas.
 *
 * Ordena e agrupa pelo `received_at`, o relógio do SERVIDOR (§3.2). As linhas
 * chegam na ordem do servidor e NÃO são reordenadas no cliente — o eixo que
 * `order` declara é respeitado por não se presumir outro (§5.3).
 */
export function LogTable({ rows }: { rows: readonly LogRow[] }) {
  const [selected, setSelected] = useState<LogRow | null>(null)

  if (rows.length === 0) {
    return (
      <EmptyState title="Nenhum evento recente para este filtro" />
    )
  }

  return (
    <>
      <table className={TABLE}>
        <thead className={THEAD}>
          <tr>
            <th scope="col" className={`${TH} w-24`}>
              tipo
            </th>
            <th scope="col" className={TH}>
              quando
            </th>
            <th scope="col" className={TH}>
              evento
            </th>
            <th scope="col" className={TH}>
              host · empresa
            </th>
            <th scope="col" className={TH}>
              assunto
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <SemanticRow key={logKey(row)} row={row} onOpen={() => setSelected(row)} />
          ))}
        </tbody>
      </table>

      {selected !== null && <EventDossier row={selected} onClose={() => setSelected(null)} />}
    </>
  )
}

function SemanticRow({ row, onOpen }: { row: LogRow; onOpen: () => void }) {
  const category = categoryPresentation(categoryOf(row.event_type))
  const what = actionLabel(row.event_type)
  const skew = skewSeconds(row)
  const skewed = skew !== null && Math.abs(skew) >= SKEW_ALARM_S

  return (
    <tr className={`${TR} cursor-pointer`} onClick={onOpen}>
      {/* Categoria: código legível é o canal primário; o tom é reforço (§4). */}
      <td className={`${TD} align-top`}>
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation()
            onOpen()
          }}
          aria-label={`Investigar evento: ${what}`}
          title={category.label}
          className="inline-flex items-center gap-1.5 text-left"
        >
          <StatusDot tone={category.tone} />
          <span className="font-mono text-micro uppercase tracking-label text-fg-secondary">
            {category.code}
          </span>
        </button>
      </td>

      <td
        className={`${TD} tnum whitespace-nowrap align-top`}
        title={`no endpoint · ${formatDayClock(row.event_time)} · recebido ${formatDayClock(row.received_at)} UTC`}
      >
        <span className="inline-flex items-center gap-1">
          {formatClock(row.event_time)}
          {skewed && (
            <span
              className="inline-flex items-center gap-1 text-micro text-warn"
              title="Relógio do endpoint divergente do servidor"
            >
              <StatusDot tone="warn" />
              {skew !== null && skew > 0 ? `+${skew}s` : `${skew}s`}
            </span>
          )}
        </span>
      </td>

      {/* O QUE aconteceu: rótulo semântico em cima, token cru embaixo. */}
      <td className={`${TD} align-top`}>
        <span className="block text-fg">{what}</span>
        <span className="block font-mono text-micro text-fg-muted">{row.event_type}</span>
      </td>

      <td className={`${TD} max-w-56 align-top`}>
        <span className="block truncate text-fg" title={row.hostname ?? row.endpoint_id}>
          {row.hostname ?? <span className="text-fg-muted">host desconhecido</span>}
        </span>
        <span
          className="block truncate text-micro text-fg-muted"
          title={row.tenant_name ?? row.tenant_id}
        >
          {row.tenant_name ?? <span className="italic">empresa não resolvida</span>}
        </span>
      </td>

      <td className={`${TD} whitespace-nowrap align-top`}>
        <span className="font-mono text-fg-secondary">
          {row.source ?? NO_VALUE}
          {row.native_id !== null && (
            <>
              <span className="text-fg-muted"> · </span>
              <span className="text-fg" title="Event ID / identificador na origem">
                {row.native_id}
              </span>
            </>
          )}
        </span>
      </td>
    </tr>
  )
}

/** Exportado para o histórico decidir a nota do eixo a partir de `order`. */
export function OrderAxisNote({ order }: { order: string }) {
  const byArrival = order === 'id_desc'
  return (
    <Notice tone="neutral">
      Ordenado por{' '}
      <span className="font-mono">{byArrival ? 'chegada (id)' : 'horário do endpoint'}</span>.
    </Notice>
  )
}
